import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { FloatingTooltip } from "../../../../common/components/Tooltip/FloatingTooltip";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";

export interface SessionsPerMonthChartBaseProps {
  data: unknown[];
  hasData: boolean;
  periodText: string;
  canGoNext: boolean;
  goToPrevMonth: () => void;
  goToNextMonth: () => void;
  goToPrevYear: () => void;
  goToNextYear: () => void;
  children: ReactNode;
}

const SessionsPerMonthChartBase = ({
  data,
  hasData,
  periodText,
  canGoNext,
  goToPrevMonth,
  goToNextMonth,
  goToPrevYear,
  goToNextYear,
  children,
}: SessionsPerMonthChartBaseProps) => {
  const { t } = useTranslation();

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          <FloatingTooltip content={t("statsNew.charts.prevYear")} ariaHidden>
            <button
              onClick={goToPrevYear}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              aria-label={t("statsNew.charts.prevYear")}
            >
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            </button>
          </FloatingTooltip>
          <FloatingTooltip content={t("statsNew.charts.prevMonth")} ariaHidden>
            <button
              onClick={goToPrevMonth}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              aria-label={t("statsNew.charts.prevMonth")}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          </FloatingTooltip>
        </div>

        <span className="text-sm text-gray-500 font-medium">{periodText}</span>

        <div className="flex items-center gap-1">
          <FloatingTooltip content={t("statsNew.charts.nextMonth")} ariaHidden wrap>
            <button
              onClick={goToNextMonth}
              disabled={!canGoNext}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t("statsNew.charts.nextMonth")}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </FloatingTooltip>
          <FloatingTooltip content={t("statsNew.charts.nextYear")} ariaHidden wrap>
            <button
              onClick={goToNextYear}
              disabled={!canGoNext}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t("statsNew.charts.nextYear")}
            >
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </button>
          </FloatingTooltip>
        </div>
      </div>

      {!hasData ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          {t("statsNew.charts.noData")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%" className="flex-1">
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              allowDecimals={false}
              width={30}
            />
            {children}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default SessionsPerMonthChartBase;
